import {
  FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { keyBy, reduce, uniqBy } from "lodash-es";

import { getPermissionTypes, permissionTypesSelector } from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import {
  IPermissionForKey,
  IPermissionGroup,
  IPermissionKey,
  IPermissionType,
} from "shared/types";
import { IUserPermissions, mapUserPermissions } from "shared/utils";
import { useBandContext } from ".";
import { loggerService } from "shared/services";

interface PermissionsContextState {
  permissionTypes: IPermissionType[] | null;
  rootGroups: IPermissionGroup[] | null;
  rootGroupPermissions: { [rootGroupId: string]: IPermissionType[] } | null;
  childrenGroups: (IPermissionGroup | IPermissionType)[][] | null;
  groupDependencies: { [groupId: string]: string[] } | null;
  disabledDependencies: { [permissionTypePath: string]: string[] } | null;
  permissionTypesMap: { [permissionTypePath: string]: IPermissionType } | null;
  bandPermissions: IUserPermissions;
  bandPermissionsArray: IPermissionForKey[];
  userCanManageBand: boolean;
  userCanAddEvent: boolean;
  userCanManageMembers: boolean;
}

const initialState: PermissionsContextState = {
  permissionTypes: [],
  rootGroups: [],
  rootGroupPermissions: {},
  childrenGroups: [],
  groupDependencies: {},
  disabledDependencies: {},
  permissionTypesMap: {},
  bandPermissions: {},
  bandPermissionsArray: [],
  userCanManageBand: false,
  userCanAddEvent: false,
  userCanManageMembers: false,
};

const PermissionsContext = createContext<PermissionsContextState>(initialState);

export const PermissionsProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const permissionTypes = useSelector(permissionTypesSelector);
  const { bandId } = useBandContext();

  useEffect(() => {
    if (!bandId) return;

    dispatch(getPermissionTypes(bandId));
  }, [dispatch, bandId]);

  // array of all top permission groups eg. BAND, MEMBERS, EVENT, EVENT: PEOPLE BLOCK
  // IPermissionGroup[]
  const rootGroups = useMemo(
    () =>
      permissionTypes
        ? uniqBy(
            reduce(
              permissionTypes,
              (groups, permission) => {
                const root =
                  permission.permissionGroup?.parentGroup ||
                  permission.permissionGroup;

                if (root) {
                  groups.push(root);
                }

                return groups;
              },
              [] as IPermissionGroup[]
            ),
            "id"
          )
        : null,
    [permissionTypes]
  );

  // array of all children groups of rootGroups based on index
  // rootGroups[0] => childrenGroups[0]
  // child can be a IPermissionGroup or a IPermissionType
  const childrenGroups = useMemo(
    () =>
      rootGroups
        ? reduce(
            permissionTypes,
            (groups, permission) => {
              const root = permission.permissionGroup?.parentGroup;
              const child = permission.permissionGroup;
              const rootIndex = rootGroups.findIndex(
                ({ id }) => id === root?.id
              );

              if (child && root && rootIndex > -1) {
                groups[rootIndex] = [...(groups[rootIndex] || []), child];
              }

              const childIndex = rootGroups.findIndex(
                ({ id }) => id === child?.id
              );

              if (!root && childIndex > -1) {
                groups[childIndex] = [
                  ...(groups[childIndex] || []),
                  permission,
                ];
              }

              return groups;
            },
            rootGroups.map(() => []) as (IPermissionGroup | IPermissionType)[][]
          ).map((children) => uniqBy(children, "id"))
        : null,
    [permissionTypes, rootGroups]
  );

  // all permission keyed by root group id
  const rootGroupPermissions = useMemo(
    () =>
      reduce(
        rootGroups,
        (result, { id: rootGroupId }) => ({
          ...result,
          [rootGroupId]: permissionTypes.filter(
            (pType) =>
              pType.permissionGroup?.id === rootGroupId ||
              pType.permissionGroup?.parentGroup?.id === rootGroupId
          ),
        }),
        {}
      ),
    [permissionTypes, rootGroups]
  );

  // object of all dependencies of a group, keyed by group id
  // { [groupId]: [permissionTypePath, permissionTypePath, permissionTypePath] }
  // eg. View event (group) = View all event + View attending events (dependencies)
  const groupDependencies = useMemo(
    () =>
      reduce(
        permissionTypes,
        (dependencies, permission) => {
          const root = permission.permissionGroup?.parentGroup;
          const child = permission.permissionGroup;

          if (root && child) {
            dependencies[child.id] = [
              ...(dependencies[child.id] || []),
              API_ENDPOINTS.PERMISSION_TYPE(permission.id),
            ];
          }

          return dependencies;
        },
        {} as { [groupId: string]: string[] }
      ),
    [permissionTypes]
  );

  // map for each permission type, when field should be disabled
  // disabled = backwards dependency is checked
  const disabledDependencies = useMemo(() => {
    let uncheck: { [permissionTypeId: string]: string[] } = {};

    // add backwards dependencies for each permission
    permissionTypes?.forEach((permission) => {
      permission.dependencies?.forEach(({ id: dependencyId }) => {
        uncheck[dependencyId] = [
          ...(uncheck[dependencyId] || []),
          API_ENDPOINTS.PERMISSION_TYPE(permission.id),
        ];
      });
    });
    return uncheck;
  }, [permissionTypes]);

  const permissionTypesMap = useMemo(
    () => keyBy(permissionTypes, ({ id }) => API_ENDPOINTS.PERMISSION_TYPE(id)),
    [permissionTypes]
  );

  // band permissions
  const { band } = useBandContext();
  const bandPermissions = useMemo(
    () => (band?.permissions ? mapUserPermissions(band.permissions) : {}),
    [band?.permissions]
  );

  const bandPermissionsArray = useMemo(
    () => band?.permissions || [],
    [band?.permissions]
  );

  const userCanManageBand = useMemo(
    () =>
      !!bandPermissions?.[IPermissionKey.edit] ||
      !!bandPermissions?.[IPermissionKey.delete],
    [bandPermissions]
  );

  const userCanAddEvent = useMemo(
    () => !!bandPermissions?.[IPermissionKey.createEvent],
    [bandPermissions]
  );

  const userCanManageMembers = useMemo(
    () => !!bandPermissions?.[IPermissionKey.addUser],
    [bandPermissions]
  );

  // ❌ debug
  const renderCount = useRef<number>(0);
  useEffect(() => {
    renderCount.current = renderCount.current + 1;

    loggerService.log({
      icon: "🔄",
      title: "Renders: PERMISSION TYPES",
      body: {
        renderCount: renderCount.current,
        permissionTypes,
      },
    });
  }, [permissionTypes]);

  return (
    <PermissionsContext.Provider
      value={{
        permissionTypes,
        rootGroups,
        rootGroupPermissions,
        childrenGroups,
        groupDependencies,
        disabledDependencies,
        permissionTypesMap,
        bandPermissions,
        bandPermissionsArray,
        userCanManageBand,
        userCanAddEvent,
        userCanManageMembers,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
